<template>
  <article id="one-stop-process">
    <h1>一站式处理</h1>

    <section id="one-stop-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"一站式处理"模块是本软件面向用户简化操作流程而特别设计的功能。它将辐射定标、大气校正、几何校正三大核心处理环节整合于一个模块中，用户仅需一次操作，即可从原始影像自动生成具有地理参考的地表反射率产品。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;该功能特别适合大批量、多景、多源遥感影像的自动处理任务，极大地提升了操作效率，降低了对遥感处理专业知识的依赖。
      </p>
    </section>

    <section id="one-stop-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/yizhanshi.png" alt="一站式处理界面" class="software-image" />
          <p class="image-caption">图3：一站式处理界面 - 集成辐射定标、大气校正、几何校正的自动化处理流程</p>
        </div>
      </div>
      <p>📷 界面组成如下：</p>
      <ul>
        <li>原始影像导入区域（支持文件夹批量导入）</li>
        <li>自动识别卫星型号与传感器</li>
        <li>输出格式与路径设置</li>
        <li>DEM/参数可选导入区域（可忽略）</li>
        <li>"一键开始处理"按钮</li>
        <li>实时进度条与结果提示</li>
      </ul>
      <p class="note">🔄 一站式处理模块将三个核心处理步骤整合为一个自动化流程，用户只需选择输入影像和输出路径，系统将自动完成所有处理步骤。</p>
    </section>

    <section id="one-stop-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数项</th>
            <th>说明</th>
            <th>设置方式</th>
            <th>示例值</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入路径</td>
            <td>原始影像所在文件夹或单景文件路径</td>
            <td>手动选择</td>
            <td>D:\raw\GF1\</td>
          </tr>
          <tr>
            <td>自动识别</td>
            <td>软件自动判断卫星/传感器/拍摄时间等</td>
            <td>自动完成</td>
            <td>GF-1_WFV_20230510</td>
          </tr>
          <tr>
            <td>输出路径</td>
            <td>成品影像与日志文件保存目录</td>
            <td>手动或默认设置</td>
            <td>D:\output\result\</td>
          </tr>
          <tr>
            <td>输出格式</td>
            <td>默认 GeoTIFF 地理参考格式</td>
            <td>固定</td>
            <td>.tif</td>
          </tr>
          <tr>
            <td>使用 DEM（可选）</td>
            <td>是否使用高程辅助几何校正</td>
            <td>默认自动判断</td>
            <td>使用 SRTM 90m DEM</td>
          </tr>
          <tr>
            <td>日志记录</td>
            <td>是否保存详细运行日志与参数快照</td>
            <td>默认开启</td>
            <td>GF1_L2_log.json</td>
          </tr>
        </tbody>
      </table>
      <p class="note">🔁 所有处理参数由系统根据影像元数据和内置信息库自动生成，无需人工设置</p>
    </section>

    <section id="one-stop-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>选择原始影像</strong>
          <ul>
            <li>支持选择单个影像或整个文件夹；</li>
            <li>可自动识别 .tif、.img、.hdf、.jp2 等主流格式。</li>
          </ul>
        </li>
        <li>
          <strong>设置输出路径</strong>
          <ul>
            <li>设置输出目录，系统将在其中创建处理结果子文件夹。</li>
          </ul>
        </li>
        <li>
          <strong>点击"一键处理"</strong>
          <ul>
            <li>系统自动依次执行：</li>
            <li>辐射定标：读取增益/偏置，计算辐亮度；</li>
            <li>大气校正：调用 MODTRAN 模型获取大气参数，计算地表反射率；</li>
            <li>几何校正：加载 RPC/轨道/DEM，绑定地理坐标系。</li>
          </ul>
        </li>
        <li>
          <strong>查看处理进度与日志</strong>
          <ul>
            <li>实时显示每步执行状态；</li>
            <li>如遇异常，系统提示错误原因并保留日志供排查。</li>
          </ul>
        </li>
        <li>
          <strong>输出完成提示</strong>
          <ul>
            <li>系统自动弹出"处理完成"提示；</li>
            <li>所有结果文件与日志按影像编号自动命名。</li>
          </ul>
        </li>
      </ol>
    </section>

    <section id="one-stop-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>文件名或内容示例</th>
            <th>文件类型</th>
            <th>描述说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.tif</td>
            <td>GeoTIFF影像</td>
            <td>完成所有处理后的地表反射率产品，含空间参考信息</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.log</td>
            <td>日志文件</td>
            <td>记录完整处理流程、调用参数、处理时间、警告信息等</td>
          </tr>
          <tr>
            <td>meta_GF1_WFV_all.json</td>
            <td>参数文件</td>
            <td>包括定标系数、大气参数、角度信息等</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.kml（可选）</td>
            <td>可视化结果</td>
            <td>用于 Google Earth 中浏览校正结果（若开启该选项）</td>
          </tr>
          <tr>
            <td>error_report.txt（可选）</td>
            <td>错误报告</td>
            <td>若有部分失败影像，自动汇总异常信息</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 该模块适合于影像数量多、处理人员非专业或时间紧急的应用场景。</p>
      <p class="note">✅ 结果输出后可直接用于 ENVI、QGIS、ArcGIS 等平台进行可视化分析与后续反演。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'OneStopProcess',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
ol {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
.main-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.main-content th,
.main-content td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2);
  color: var(--text-primary);
}

.main-content thead tr {
  background-color: rgba(var(--primary), 0.2);
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

.main-content tbody tr {
  background-color: rgba(var(--card-bg), 0.6);
}

.main-content tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8);
}

.main-content tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
.main-content ol,
.main-content ul {
  list-style: none;
  padding-left: 20px;
  margin: 20px 0;
}

.main-content ol li,
.main-content ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

/* 新增：将 counter-increment 添加到 ol li */
ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary);
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li::before {
  content: "\2705";
  color: var(--secondary);
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li p {
  margin-left: 0;
  margin-bottom: 5px;
}

.main-content p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style> 